import type {
  NavioBuildConflictPolicy,
  NavioBuildOfflineEntity,
  NavioBuildQueuedOperation,
} from '../../../../../packages/shared/src/types/naviobuild-offline';

export const CACHE_STORE = 'cache';
export const QUEUE_STORE = 'queue';
export const CHECKPOINT_STORE = 'checkpoints';
export const KEY_STORE = 'keys';
export const OFFLINE_CHANGE_EVENT = 'naviobuild:offline-change';
export const DEFAULT_VISIBLE_QUEUE_STATUSES: readonly OfflineQueuedOperationStatus[] = [
  'queued',
  'failed',
  'conflict',
];

export type OfflineQueuedOperationStatus = 'queued' | 'syncing' | 'failed' | 'conflict' | 'synced';

export interface OfflineCacheRecord<T = unknown> {
  key: string;
  projectId: string | null;
  routeId: string | null;
  data: T;
  updatedAt: string;
}

export interface OfflineQueueRecord extends NavioBuildQueuedOperation {
  status: OfflineQueuedOperationStatus;
  retryCount: number;
  updatedAt: string;
  lastError: string | null;
  nextRetryAt: string | null;
}

export interface QueueOperationInput {
  entity: NavioBuildOfflineEntity;
  entityId: string;
  projectId: string;
  operation: NavioBuildQueuedOperation['operation'];
  conflictPolicy: NavioBuildConflictPolicy;
  payload: Record<string, unknown>;
  actorUserId: string;
}

export interface OfflineStoreAdapter {
  getCache<T>(key: string): Promise<OfflineCacheRecord<T> | null>;
  setCache<T>(record: OfflineCacheRecord<T>): Promise<void>;
  listQueuedOperations(statuses?: readonly OfflineQueuedOperationStatus[]): Promise<OfflineQueueRecord[]>;
  queueOperation(input: QueueOperationInput): Promise<OfflineQueueRecord>;
  updateOperation(id: string, patch: Partial<OfflineQueueRecord>): Promise<void>;
  removeOperation(id: string): Promise<void>;
}

export function nowIso(): string {
  return new Date().toISOString();
}

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function emitOfflineChange(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OFFLINE_CHANGE_EVENT));
  }
}

export function createQueueRecord(input: QueueOperationInput): OfflineQueueRecord {
  const id = createId();
  const timestamp = nowIso();

  return {
    ...input,
    id,
    status: 'queued',
    retryCount: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
    lastError: null,
    nextRetryAt: null,
    idempotencyKey: `naviobuild:${input.projectId}:${input.entity}:${input.entityId}:${input.operation}:${id}`,
  };
}
